import { ApiError, toApiError } from '@/lib/api-errors';
import {
  getAccessToken,
  setSession,
  triggerUnauthorized,
} from '@/lib/session';

export const API_BASE = import.meta.env.VITE_API_URL ?? '';

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const response = await fetch(`${API_BASE}/api/auth/refresh/internal`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) return false;

  const tokens = (await response.json()) as {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
  };

  if (!tokens.accessToken) return false;

  setSession(tokens);
  return true;
}

export async function tryRefreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        // Refresh token is in httpOnly cookie — sent automatically via credentials: 'include'
        return await doRefresh();
      } catch {
        // Network error (e.g. transient Azure infrastructure blip) — retry once after a short delay
        await new Promise((r) => setTimeout(r, 300));
        try {
          return await doRefresh();
        } catch {
          return false;
        }
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

function withAuthHeaders(init?: RequestInit): RequestInit {
  const token = getAccessToken();
  const headers = new Headers(init?.headers ?? {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

export async function apiRequest<T>(
  path: string,
  init?: RequestInit,
  options?: { retryOn401?: boolean }
): Promise<T> {
  const retryOn401 = options?.retryOn401 ?? true;
  const response = await fetch(`${API_BASE}${path}`, withAuthHeaders(init));

  if (response.status === 401 && retryOn401) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      return apiRequest<T>(path, init, { retryOn401: false });
    }

    await triggerUnauthorized();
    throw new ApiError('Session expired. Please sign in again.', 401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
}

export async function apiRequestVoid(
  path: string,
  init?: RequestInit,
  options?: { retryOn401?: boolean }
) {
  await apiRequest<null>(path, init, options);
}

export async function apiRequestBlob(
  path: string,
  init?: RequestInit,
  options?: { retryOn401?: boolean }
): Promise<{ blob: Blob; filename?: string | null; headers: Headers }> {
  const retryOn401 = options?.retryOn401 ?? true;
  const response = await fetch(`${API_BASE}${path}`, withAuthHeaders(init));

  if (response.status === 401 && retryOn401) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      return apiRequestBlob(path, init, { retryOn401: false });
    }

    await triggerUnauthorized();
    throw new ApiError('Session expired. Please sign in again.', 401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  const disposition = response.headers.get('content-disposition');
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] ?? null;
  const blob = await response.blob();
  return { blob, filename, headers: response.headers };
}
